import { useEffect, useState } from 'react';
import {
    Eye,
    ChevronLeft,
    ChevronRight,
    Loader2,
    ArrowUpRight,
    ArrowDownLeft,
    RotateCcw,
    User,
    ExternalLink,
    X,
    Circle,
    FileText
} from 'lucide-react';
import { apiClient } from '../../lib/api';

// ----------------------------------------------------------------------
// 1. Interfaces
// ----------------------------------------------------------------------
interface TransactionDoc {
    docId: number;
    docTypeName: string;
    docLink: string;
    description: string;
}

interface TransactionItem {
    transactionId: number;
    userId: number;
    userName: string;
    amount: number;
    referenceNumber: string;
    fechaTransaccion: string;
    status: string; // "COMPLETED", "PENDING", "FAILED"
    type: 'HOST' | 'PORTAL' | 'REFUND'; // Transferencia al anfitrión, al portal, devoluciones
    documents: TransactionDoc[];
}

// ----------------------------------------------------------------------
// 2. Componente Principal
// ----------------------------------------------------------------------
const TransaccionesPage = () => {

    // --- ESTADOS ---
    const [rows, setRows] = useState<TransactionItem[]>([]);
    const [loading, setLoading] = useState(true);
    const [activeTab, setActiveTab] = useState<string>('HOST'); // HOST, PORTAL, REFUND
    const [currentPage, setCurrentPage] = useState(1);
    const [pagination, setPagination] = useState<any>(null);
    const pageSize = 10;
    const [openModal, setOpenModal] = useState(false);
    const [selectedTx, setSelectedTx] = useState<TransactionItem | null>(null);

    // ----------------------------------------------------------------------
    // 3. EFECTOS (CARGA DE DATOS)
    // ----------------------------------------------------------------------
    const fetchTransactions = async () => {
        setLoading(true);
        try {
            // Ajustar endpoint según tu API de transacciones
            const endpoint = `GetTransactionsByType?type=${activeTab}&pageNumber=${currentPage}&pageSize=${pageSize}`;
            const response = await apiClient.get<any>(endpoint);
            const result = response?.data || response || [];
            setRows(result);
            setPagination(response.pagination);
        } catch (error) {
            console.error("Error al cargar transacciones:", error);
            setRows([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchTransactions();
    }, [activeTab, currentPage]);

    // --- HANDLERS ---
    const handleTabChange = (tabId: string) => {
        setActiveTab(tabId);
        setCurrentPage(1);
    };

    const handleOpenDetail = (tx: TransactionItem) => {
        setSelectedTx(tx);
        setOpenModal(true);
    };

    // --- CONFIGURACIÓN DE TABS ---
    const tabs = [
        { id: 'HOST', label: "Transferencias al Anfitrión", icon: <ArrowUpRight size={18} /> },
        { id: 'PORTAL', label: "Transferencias al Portal", icon: <ArrowDownLeft size={18} /> },
        { id: 'REFUND', label: "Devoluciones", icon: <RotateCcw size={18} /> },
    ];

    const getStatusStyles = (status: string) => {
        switch (status) {
            case 'COMPLETED': return { color: 'text-green-500', label: 'Completado' };
            case 'PENDING': return { color: 'text-orange-500', label: 'Procesando' };
            case 'FAILED': return { color: 'text-red-500', label: 'Fallido' };
            default: return { color: 'text-gray-500', label: status };
        }
    };

    return (
        <div className="space-y-6 relative">
            {/* HEADER */}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                <div>
                    <h1 className="text-3xl font-heading font-bold text-foreground">Transacciones ACH</h1>
                    <p className="text-muted-foreground mt-1">Monitoreo y gestión de movimientos financieros de la pasarela de pago</p>
                </div>
            </div>

            {/* TABS DE FILTROS */}
            <div className="border-b border-border">
                <div className="flex gap-8 overflow-x-auto no-scrollbar">
                    {tabs.map((tab) => (
                        <button
                            key={tab.id}
                            onClick={() => handleTabChange(tab.id)}
                            className={`flex items-center gap-2 py-4 px-1 border-b-2 text-sm font-medium transition-all ${
                                activeTab === tab.id
                                ? "border-primary text-primary"
                                : "border-transparent text-muted-foreground hover:text-foreground"
                            }`}
                        >
                            {tab.icon}
                            {tab.label}
                        </button>
                    ))}
                </div>
            </div>

            <div className="bg-card rounded-soft border border-border p-6 shadow-soft min-h-[500px]">
                <div className="overflow-x-auto">
                    <table className="w-full">
                        <thead className="border-b border-border">
                            <tr>
                                <th className="text-left py-3 px-4 text-xs font-semibold text-muted-foreground uppercase">Transacción ID</th>
                                <th className="text-left py-3 px-4 text-xs font-semibold text-muted-foreground uppercase">Referencia Bancaria</th>
                                <th className="text-left py-3 px-4 text-xs font-semibold text-muted-foreground uppercase">Estado</th>
                                <th className="text-left py-3 px-4 text-xs font-semibold text-muted-foreground uppercase">Monto</th>
                                <th className="text-left py-3 px-4 text-xs font-semibold text-muted-foreground uppercase">Moneda</th>
                                <th className="text-left py-3 px-4 text-xs font-semibold text-muted-foreground uppercase">Fecha de liquidación</th>
                                <th className="text-left py-3 px-4 text-xs font-semibold text-muted-foreground uppercase">Hora de liquidación</th>
                                <th className="text-right py-3 px-4 text-xs font-semibold text-muted-foreground uppercase">Mensaje</th>
                                <th className="text-right py-3 px-4 text-xs font-semibold text-muted-foreground uppercase">Host</th>
                                <th className="text-right py-3 px-4 text-xs font-semibold text-muted-foreground uppercase">Propiedad</th>
                                <th className="text-right py-3 px-4 text-xs font-semibold text-muted-foreground uppercase">Acción</th>
                            </tr>
                        </thead>

                        <tbody className="divide-y divide-border">
                            {loading ? (
                                <tr>
                                    <td colSpan={11} className="py-20 text-center">
                                        <div className="flex flex-col items-center"><Loader2 className="h-10 w-10 animate-spin text-primary mb-2" /><p>Cargando transacciones...</p></div>
                                    </td>
                                </tr>
                            ) : rows.length === 0 ? (
                                <tr>
                                    <td colSpan={11} className="py-20 text-center text-muted-foreground">
                                        <FileText className="h-10 w-10 mx-auto mb-2 opacity-20" />
                                        <p>No hay registros en esta categoría</p>
                                    </td>
                                </tr>
                            ) : (
                                rows.map((tx) => (
                                    <tr key={tx.transactionId} className="hover:bg-muted/50 transition-colors">
                                        <td className="py-4 px-4 whitespace-nowrap">
                                            <div className="flex items-center gap-2">
                                                <Circle size={8} className={`fill-current ${getStatusStyles(tx.status).color}`} />
                                                <span className="text-sm font-medium">{getStatusStyles(tx.status).label}</span>
                                            </div>
                                        </td>
                                        <td className="py-4 px-4 text-sm text-muted-foreground">
                                            {new Date(tx.fechaTransaccion).toLocaleDateString()}
                                        </td>
                                        <td className="py-4 px-4 text-sm font-mono">{tx.referenceNumber}</td>
                                        <td className="py-4 px-4">
                                            <div className="flex items-center gap-2">
                                                <div className="h-7 w-7 rounded-full bg-muted flex items-center justify-center text-muted-foreground"><User size={14} /></div>
                                                <span className="text-sm font-medium">{tx.userName}</span>
                                            </div>
                                        </td>
                                        <td className="py-4 px-4 text-sm font-bold">
                                            ${tx.amount.toLocaleString('en-US', { minimumFractionDigits: 2 })}
                                        </td>
                                        <td className="py-4 px-4 text-right">
                                            <button 
                                                onClick={() => handleOpenDetail(tx)}
                                                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold rounded-soft bg-secondary text-secondary-foreground hover:bg-secondary/80 border border-border"
                                            >
                                                <Eye size={14} /> VER
                                            </button>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>

                {/* PAGINACIÓN */}
                {pagination && pagination.totalPageCount > 1 && (
                    <div className="flex justify-center border-t border-border mt-6 pt-6">
                        <div className="flex items-center gap-4">
                            <button disabled={!pagination.hasPrevious} onClick={() => setCurrentPage(prev => prev - 1)} className="p-2 disabled:opacity-30"><ChevronLeft /></button>
                            <span className="text-sm font-medium">Página {pagination.currentPage} de {pagination.totalPageCount}</span>
                            <button disabled={!pagination.hasNext} onClick={() => setCurrentPage(prev => prev + 1)} className="p-2 disabled:opacity-30"><ChevronRight /></button>
                        </div>
                    </div>
                )}
            </div>

            {/* MODAL DE DETALLE (Igual al de Identidad) */}
            {openModal && selectedTx && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
                    <div className="bg-card w-full max-w-4xl max-h-[90vh] overflow-y-auto rounded-soft border border-border shadow-2xl flex flex-col">
                        <div className="flex items-center justify-between p-6 border-b border-border sticky top-0 bg-card z-10">
                            <h2 className="text-xl font-bold">Comprobante de Transacción</h2>
                            <button onClick={() => setOpenModal(false)} className="p-2 hover:bg-muted rounded-full"><X size={20} /></button>
                        </div>
                        
                        <div className="p-6 space-y-6">
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                <div className="p-4 bg-muted/30 rounded-soft border border-border text-center">
                                    <p className="text-xs text-muted-foreground font-semibold uppercase">Monto</p>
                                    <p className="text-2xl font-black text-primary">${selectedTx.amount}</p>
                                </div>
                                <div className="p-4 bg-muted/30 rounded-soft border border-border text-center">
                                    <p className="text-xs text-muted-foreground font-semibold uppercase">Referencia</p>
                                    <p className="text-lg font-bold">{selectedTx.referenceNumber}</p>
                                </div>
                                <div className="p-4 bg-muted/30 rounded-soft border border-border text-center">
                                    <p className="text-xs text-muted-foreground font-semibold uppercase">Usuario</p>
                                    <p className="text-lg font-bold">{selectedTx.userName}</p>
                                </div>
                            </div>

                            <div className="bg-muted/10 border border-border rounded-soft p-4 min-h-[300px] flex items-center justify-center">
                                {selectedTx.documents.length > 0 ? (
                                    selectedTx.documents.map((doc, idx) => (
                                        <div key={idx} className="flex flex-col items-center">
                                            <div className="w-full bg-white rounded-lg border border-border overflow-hidden p-2 shadow-sm">
                                                <img src={doc.docLink} alt="Comprobante" className="max-h-[400px] object-contain" />
                                            </div>
                                            <a href={doc.docLink} target="_blank" rel="noreferrer" className="mt-3 text-xs font-bold text-primary flex items-center gap-1 hover:underline">
                                                Ver comprobante original <ExternalLink size={12} />
                                            </a>
                                        </div>
                                    ))
                                ) : (
                                    <p className="text-muted-foreground">No hay comprobante digital adjunto.</p>
                                )}
                            </div>
                        </div>

                        <div className="p-6 border-t border-border bg-muted/10 text-right">
                            <button onClick={() => setOpenModal(false)} className="px-6 py-2.5 rounded-soft bg-primary text-primary-foreground font-bold hover:bg-primary/90">
                                Cerrar
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default TransaccionesPage;